import { ComponentFactoryResolver, Injectable, Injector, inject } from '@angular/core';

import { ContractType } from '@app-model/api';
import { QuotePrintTemplateComponent, RemitPrintTemplateComponent } from '@app-shared/components';
import html2pdf from 'html2pdf.js';
import { Observable, Subscriber, take } from 'rxjs';

import { DocumentRef } from '../reference/document.reference';

@Injectable({
  providedIn: 'root',
})
export class TemplateService {
  private componentFactoryResolver = inject(ComponentFactoryResolver);
  private injector = inject(Injector);
  private docRef = inject(DocumentRef);

  printTemplate(printQuoteHtmlString: string, orientation: 'p' | 'l'): void {
    // create HTML Document
    const htmlDoc = this.docRef.nativeDocument.implementation.createHTMLDocument('quote');

    // create body element with div and append to html document
    const bodyElement = this.docRef.nativeDocument.createElement('div');
    bodyElement.innerHTML = printQuoteHtmlString;
    htmlDoc.body.appendChild(bodyElement);

    this._downloadPDF(htmlDoc.body, orientation);
  }

  private _downloadPDF(html: HTMLElement, orientation: 'p' | 'l'): void {
    const pdfOrientation: 'portrait' | 'landscape' = orientation === 'p' ? 'portrait' : 'landscape';
    const opt = {
      margin: 1,
      image: { type: 'jpeg' as const, quality: 1 },
      html2canvas: { scale: 2, allowTaint: true },
      jsPDF: { unit: 'in' as const, format: 'letter' as const, orientation: pdfOrientation },
    };

    html2pdf()
      .from(html)
      .set(opt)
      .toPdf()
      .output('bloburl')
      .then((blob) => window.open(blob, '_blank'));
  }

  printRemit(contracts: ContractType[] = [], remitted: boolean, navbarLogo: string, totalDealerCost: number): void {
    this._convertImage(navbarLogo).subscribe((imgData: string | ArrayBuffer) => {
      const factory = this.componentFactoryResolver.resolveComponentFactory(RemitPrintTemplateComponent);
      const printRemitTemplate = factory.create(this.injector);
      printRemitTemplate.instance.contracts.set(contracts);
      printRemitTemplate.instance.remitted.set(remitted);
      printRemitTemplate.instance.navbarLogo.set(imgData);
      printRemitTemplate.instance.totalDealerCost.set(totalDealerCost);
      printRemitTemplate.changeDetectorRef.detectChanges();
      this.printTemplate(printRemitTemplate.location.nativeElement.innerHTML, 'l');
    });
  }

  printQuote(navbarLogo: string): void {
    this._convertImage(navbarLogo).subscribe((imgData: string | ArrayBuffer) => {
      const factory = this.componentFactoryResolver.resolveComponentFactory(QuotePrintTemplateComponent);
      const printQuoteTemplate = factory.create(this.injector);
      printQuoteTemplate.instance.navbarLogo = imgData;
      printQuoteTemplate.changeDetectorRef.detectChanges();
      this.printTemplate(printQuoteTemplate.location.nativeElement.innerHTML, 'p');
    });
  }

  private _convertImage(url: string): Observable<string | ArrayBuffer> {
    return new Observable((observer: Subscriber<string | ArrayBuffer>) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      xhr.onload = () => {
        const reader: FileReader = new FileReader();
        reader.onloadend = () => {
          observer.next(reader.result);
          observer.complete();
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.send();
    }).pipe(take(1));
  }
}
